/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Icons from "lucide-react";

type IconName = keyof typeof Icons;

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  Fragment,
  ReactNode,
} from "react";
import { useEffectEvent } from "@/hooks/use-effect-event";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import { useLocation } from "@tanstack/react-router";
import { getModuleIdForRoute, getModuleIcon } from "@/convex/lib/modules";
import { getIconComponent } from "@/lib/modules";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Table, TableFooter, TableRow, TableCell } from "@/components/ui/table";
import {
  useColumnVisibility,
  type ExternalColumnVisibility,
} from "@/hooks/useColumnVisibility";
import { useColumnOrder } from "@/hooks/useColumnOrder";
import { useColumnPin } from "@/hooks/useColumnPin";
import { useSorting } from "@/hooks/useSortingTable";
import {
  useAggregations,
  computeAggregation,
  type AggregationResult,
} from "@/hooks/useAggregations";
import ColumnVisibilityToggle from "./column-visibility-toggle";
import AggregationsToggle from "./aggregations-toggle";
import AggregationsBar from "./aggregations-bar";
import AggregationPill from "./aggregation-pill";
import { TableViewToggle } from "./table-view-toggle";
import { useTableViewMode } from "@/hooks/useTableViewMode";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LucideIcon,
  Inbox,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Checkbox } from "@/components/ui/checkbox";
import AdvancedPagination from "./pagination-table";
import { useSkeletonColumns } from "./skeleton-columns";
import SkeletonTableBody from "./skeleton-table-body";
import BulkActionBar, { type BulkAction } from "./bulk-action-bar";
export type { BulkAction } from "./bulk-action-bar";
import SkeletonCardsMobile from "./skeleton-cards-mobile";
import SkeletonCardsGrid from "./skeleton-cards-grid";
import {
  TableColumnGeneric as OptimizedTableColumn,
  TableActionGeneric as OptimizedTableAction,
} from "@/hooks/table/types";
import DataTableBody from "./data-table-body";
import DataTableHeader from "./data-table-header";
import { withSortableLastModified } from "./last-modified-column";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../ui/empty";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Define the action type properly - this is NOT a ReactNode, it's a configuration object
export interface TableAction<T = any> extends OptimizedTableAction<T> {
  // Esta interfaz extiende OptimizedTableAction para compatibilidad
}

// Define column type
export interface TableColumn<T = any> extends OptimizedTableColumn<T> {
  // Esta interfaz extiende OptimizedTableColumn para compatibilidad
}

// Re-export optimized types for new usage
export type { OptimizedTableColumn, OptimizedTableAction };

export interface DataTablePaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** totalCount es una cota inferior (el servidor no contó todas las filas). */
  totalCountIsLowerBound?: boolean;
}

// Define the props for the DataTable
export interface DataTableProps<T = any> {
  icon?: IconName;
  /**
   * Override del moduleId del sidebar. Si no se pasa, se infiere de la ruta
   * actual con getModuleIdForRoute y se usa su icono registrado en
   * ERP_MODULES_CONFIG (con fallback al icono del grupo).
   */
  moduleId?: string;
  /**
   * Icono del encabezado (nombre de lucide) que gana sobre el del módulo; p. ej.
   * el icono configurado en una colección.
   */
  headerIcon?: string;
  title?: string;
  description?: string;
  columns: TableColumn<T>[];
  data: T[] | Record<string, T[]>;
  actions?: TableAction<T>[];
  rowActions?: (row: T) => TableAction<T>[];
  isLoading?: boolean;
  /**
   * Las columnas aun no se conocen (llegan con otra consulta): el esqueleto
   * usa las cabeceras de la ultima visita a `tableId` o columnas genericas.
   */
  columnsPending?: boolean;
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description: string;
  };
  pagination?: DataTablePaginationProps;
  className?: string;
  // ❌ ELIMINAR: onSort?: (sortConfig: SortConfig) => void;
  enableColumnVisibility?: boolean;
  /** Columnas que arrancan ocultas (ver hook `useColumnVisibility`). */
  hiddenByDefault?: ReadonlySet<string>;
  /**
   * Override reactivo de visibilidad por columna (server como fuente de
   * verdad para los system fields virtuales `_id`/`_uuid`). Tiene
   * precedencia sobre `localStorage` y `hiddenByDefault`; las claves
   * que no aparecen caen al comportamiento normal. Ver
   * `hooks/useColumnVisibility.ts`.
   */
  columnVisibilityOverride?: Readonly<Record<string, boolean>>;
  /**
   * Modo "server-side": cuando se pasa, `useColumnVisibility` deja de
   * usar `localStorage` y adopta la lista provista como fuente de
   * verdad. Los toggles del toolbar emiten el siguiente set vía
   * `onVisibilityChange` en lugar de persistir a localStorage. Se usa
   * en `collection_records` con `useCollectionColumnVisibility`.
   */
  externalColumnVisibility?: ExternalColumnVisibility;
  /** Habilita reordenar columnas por drag & drop en el header (grip en hover). */
  enableColumnReorder?: boolean;
  /** Habilita pinnear (congelar a la izquierda) columnas desde el header. */
  enableColumnPin?: boolean;
  /** Habilita agregaciones (sum/avg/count/min/max) en el header. */
  enableAggregations?: boolean;
  /** Habilita el selector de vista tabla/tarjetas (requiere tableId para persistir). */
  enableViewToggle?: boolean;
  tableId?: string;
  enableRowSelection?: boolean;
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selectedRows: Set<string | number>) => void;
  rowIdField?: string;
  bulkActions?: BulkAction<T>[];
  bulkActionsLabel?: string;
  /** Clase CSS extra por fila (tabla y card). Para distinguir filas especiales
   *  (p. ej. borradores locales sin sincronizar). */
  rowClassName?: (row: T) => string | undefined;
  /** Si devuelve false, la fila no muestra checkbox y queda fuera del
   *  "seleccionar todo" / bulk. Default: todas seleccionables. */
  isRowSelectable?: (row: T) => boolean;
  /** Sin conexión, sirviendo filas desde la réplica local: muestra un ícono con
   *  tooltip a la izquierda del switcher de vista. */
  offlineFromCache?: boolean;
}

export default function DataTable<T extends Record<string, any>>({
  title,
  description,
  columns: rawColumns,
  data,
  actions = [],
  rowActions,
  // Sin default: si el consumidor no informa estado de carga, lo dejamos
  // undefined para que AdvancedPagination use su heurística previa en vez de
  // tratar un `false` por defecto como "ya cargado" (perdería el anti-parpadeo).
  isLoading,
  columnsPending,
  emptyState,
  pagination,
  className,
  enableColumnVisibility = false,
  /**
   * Columnas que deben arrancar OCULTAS en la primera visita
   * (sin localStorage del usuario). El toggle del toolbar sigue
   * funcionando, por lo que el usuario puede activarlas
   * individualmente desde el popover. Útil para columnas
   * virtuales (ej. UUID) que no son parte del catálogo de
   * campos custom del usuario.
   */
  hiddenByDefault,
  columnVisibilityOverride,
  externalColumnVisibility,
  enableColumnReorder = false,
  enableColumnPin = false,
  enableAggregations = false,
  enableViewToggle = true,
  tableId,
  enableRowSelection = false,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  rowIdField = "_id",
  bulkActions,
  bulkActionsLabel,
  icon,
  moduleId: moduleIdProp,
  headerIcon,
  rowClassName,
  isRowSelectable,
  offlineFromCache = false,
}: DataTableProps<T>) {
  // Columna de última modificación ordenable por fecha en todas las tablas.
  const columns = useMemo(
    () => withSortableLastModified(rawColumns),
    [rawColumns],
  );
  // Hook de traducción
  const { t } = useCommonI18n();

  // Icono del módulo: prop explícita > ruta inferida. Si no hay match,
  // el header no muestra icono (fallback silencioso).
  const location = useLocation();
  const resolvedModuleId = useMemo(
    () => moduleIdProp ?? getModuleIdForRoute(location.pathname),
    [moduleIdProp, location.pathname],
  );
  const moduleIconName = useMemo(
    () => (resolvedModuleId ? getModuleIcon(resolvedModuleId) : undefined),
    [resolvedModuleId],
  );
  const HeaderIcon = useMemo(
    () =>
      (headerIcon && getIconComponent(headerIcon)) ||
      (moduleIconName ? getIconComponent(moduleIconName) : null),
    [headerIcon, moduleIconName],
  );

  // Row selection state
  const [internalSelectedRows, setInternalSelectedRows] = useState<
    Set<string | number>
  >(new Set());
  const selectedRows = externalSelectedRows ?? internalSelectedRows;

  //  OPTIMIZACIÓN 2: useCallback para handlers estables
  const handleSelectionChange = useCallback(
    (newSelection: Set<string | number>) => {
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      } else {
        setInternalSelectedRows(newSelection);
      }
    },
    [onSelectionChange],
  );

  //  OPTIMIZACIÓN 3: useCallback para getRowId (evita recrear en cada render)
  const getRowId = useCallback(
    (row: T): string | number => {
      return row[rowIdField] || row.id || row._id;
    },
    [rowIdField],
  );

  // Filas seleccionables (excluye las que `isRowSelectable` marca como no
  // seleccionables, p. ej. borradores locales). El "seleccionar todo" y el
  // estado allRowsSelected operan solo sobre este subconjunto.
  const selectableData = useMemo(
    () =>
      Array.isArray(data)
        ? isRowSelectable
          ? data.filter((row) => isRowSelectable(row))
          : data
        : [],
    [data, isRowSelectable],
  );

  //  OPTIMIZACIÓN 4: Memoizar allRowsSelected
  const allRowsSelected = useMemo(
    () =>
      selectableData.length > 0 &&
      selectableData.every((row) => selectedRows.has(getRowId(row))),
    [selectableData, selectedRows, getRowId],
  );

  //  OPTIMIZACIÓN 5: useCallback para handleSelectAll
  const handleSelectAll = useCallback(() => {
    if (allRowsSelected) {
      handleSelectionChange(new Set());
    } else {
      const allIds = selectableData.map((row) => getRowId(row));
      handleSelectionChange(new Set(allIds));
    }
  }, [selectableData, allRowsSelected, handleSelectionChange, getRowId]);

  //  OPTIMIZACIÓN 6: useCallback para handleRowSelection
  const handleRowSelection = useCallback(
    (rowId: string | number) => {
      const newSelection = new Set(selectedRows);
      if (newSelection.has(rowId)) {
        newSelection.delete(rowId);
      } else {
        newSelection.add(rowId);
      }
      handleSelectionChange(newSelection);
    },
    [selectedRows, handleSelectionChange],
  );

  // Column visibility - ya está optimizado
  const { visibleColumns, toggleColumn, resetToDefaults, isColumnVisible } =
    useColumnVisibility(
      tableId || "default-table",
      columns,
      hiddenByDefault,
      columnVisibilityOverride,
      externalColumnVisibility,
    );

  // Orden de columnas (drag & drop en el header), persistido por tableId.
  const { orderColumns, moveColumn } = useColumnOrder(tableId, columns);

  // Pin de columnas (congelar a izquierda/derecha), persistido por tableId.
  const { pinnedLeft, pinnedRight, getPinSide, setPin } = useColumnPin(
    tableId,
    columns,
  );

  // Agregaciones (sum/avg/count/min/max) persistidas por tableId en localStorage.
  // Solo se exponen agregaciones declaradas en columnas (no hidden).
  const availableAggregations = useMemo(() => {
    if (!enableAggregations) return [];
    return columns
      .filter((c) => c.aggregate && !c.aggregate.hidden)
      .map((c) => ({
        key: c.key,
        label: c.aggregate!.label ?? c.label,
        kinds: c.aggregate!.kinds,
        dataType: c.aggregate!.dataType ?? "number",
      }));
  }, [enableAggregations, columns]);
  const aggregations = useAggregations(
    enableAggregations ? tableId : undefined,
    availableAggregations.map((a) => a.key),
  );
  const hasActionsCol = (actions?.length ?? 0) > 0 || !!rowActions;

  // Alto máximo MEDIDO del contenedor de scroll de la tabla (sin dvh mágico):
  // fondo útil del <main> − top del contenido de la card − alto de la paginación.
  const cardContentRef = useRef<HTMLDivElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [scrollMaxH, setScrollMaxH] = useState<number>();

  // Offsets MEDIDOS del DOM (no estimados): { key: {side, offset} }.
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [pinOffsets, setPinOffsets] = useState<
    Record<string, { side: "left" | "right"; offset: number }>
  >({});

  // Vista (tabla | tarjetas) persistida en localStorage por tableId.
  const { viewMode, setViewMode } = useTableViewMode(
    tableId || "default-table",
  );
  const showViewToggle = enableViewToggle;
  const isCardView = showViewToggle && viewMode === "cards";

  //  OPTIMIZACIÓN 7: displayColumns con dependencias correctas
  const displayColumns = useMemo(() => {
    if (!tableId) {
      return columns;
    }
    const visible = enableColumnVisibility
      ? columns.filter((col) => visibleColumns.includes(col.key))
      : columns;
    // Aplicar el orden persistido solo si el reordenamiento está habilitado.
    const ordered = enableColumnReorder ? orderColumns(visible) : visible;
    // Pin: izquierda al frente, derecha al final (antes de acciones),
    // conservando el orden relativo. El resto queda en medio.
    if (!enableColumnPin || (pinnedLeft.length === 0 && pinnedRight.length === 0))
      return ordered;
    const leftSet = new Set(pinnedLeft);
    const rightSet = new Set(pinnedRight);
    return [
      ...ordered.filter((c) => leftSet.has(c.key)),
      ...ordered.filter((c) => !leftSet.has(c.key) && !rightSet.has(c.key)),
      ...ordered.filter((c) => rightSet.has(c.key)),
    ];
  }, [
    enableColumnVisibility,
    enableColumnReorder,
    enableColumnPin,
    pinnedLeft,
    pinnedRight,
    tableId,
    columns,
    visibleColumns,
    orderColumns,
  ]);
  const skeletonColumns =
    useSkeletonColumns(tableId, displayColumns, columnsPending) ?? displayColumns;

  // Medir el offset izquierdo real de cada columna pinneada a partir del ancho
  // renderizado de las celdas del header (evita solapes por anchos estimados).
  const pinnedSignature = useMemo(
    () => displayColumns.map((c) => c.key).join("|"),
    [displayColumns],
  );
  useLayoutEffect(() => {
    const wrapper = tableScrollRef.current;
    if (
      !wrapper ||
      !enableColumnPin ||
      (pinnedLeft.length === 0 && pinnedRight.length === 0)
    ) {
      setPinOffsets((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    const cellWidth = (key: string) => {
      const cell = wrapper.querySelector<HTMLElement>(
        `thead [data-column-key="${CSS.escape(key)}"]`,
      );
      return cell ? cell.getBoundingClientRect().width : 0;
    };
    const measure = () => {
      const offsets: Record<
        string,
        { side: "left" | "right"; offset: number }
      > = {};
      // Izquierda: acumular desde el ancho de la col de selección.
      let left = enableRowSelection ? 48 : 0;
      for (const col of displayColumns) {
        if (getPinSide(col.key) !== "left") break; // contiguas al frente
        offsets[col.key as string] = { side: "left", offset: left };
        left += cellWidth(col.key as string);
      }
      // Derecha: acumular desde el ancho de la col de acciones, de derecha a izq.
      let right = hasActionsCol ? 32 : 0; // col de acciones: w-8 = 32px
      for (let i = displayColumns.length - 1; i >= 0; i--) {
        const col = displayColumns[i];
        if (getPinSide(col.key) !== "right") break; // contiguas al final
        offsets[col.key as string] = { side: "right", offset: right };
        right += cellWidth(col.key as string);
      }
      setPinOffsets(offsets);
    };
    measure();
    const ro = new ResizeObserver(measure);
    const thead = wrapper.querySelector("thead");
    if (thead) ro.observe(thead);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // Los datos afectan el ancho auto de columnas -> re-medir al cambiar.
  }, [
    enableColumnPin,
    pinnedLeft,
    pinnedRight,
    pinnedSignature,
    getPinSide,
    enableRowSelection,
    hasActionsCol,
    data,
  ]);

  const pinnedStyles = useMemo(() => {
    const styles: Record<string, React.CSSProperties> = {};
    for (const [key, { side, offset }] of Object.entries(pinOffsets)) {
      // El z-index se maneja por clases (distinto en header vs body).
      styles[key] =
        side === "left"
          ? { position: "sticky", left: offset }
          : { position: "sticky", right: offset };
    }
    return styles;
  }, [pinOffsets]);

  // Con alguna columna pinneada a la izquierda, la columna de selección
  // (checkbox) también se fija (left-0) para no despegarse del bloque.
  const selectionSticky =
    enableColumnPin && enableRowSelection && pinnedLeft.length > 0;

  // Sorting - ya está bien
  const {
    currentSorts,
    toggleSort,
    getSortedData,
    getSortDirection,
    getSortPriority,
    isMultiSortActive,
  } = useSorting(tableId || "default-table", displayColumns, undefined, true);

  //  OPTIMIZACIÓN 8: sortedData con dependencias correctas
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return data;
    return getSortedData(data);
  }, [data, getSortedData]);

  // Medir el alto disponible para el scroll de la tabla:
  //   fondo útil del <main> − top del contenido de la card − alto de paginación
  // Se recalcula con ResizeObserver (main + paginación) y en resize de ventana.
  useLayoutEffect(() => {
    const content = cardContentRef.current;
    if (!content) return;
    const main = content.closest("main");

    const px = (v: string) => parseFloat(v || "0") || 0;
    const card = content.closest('[data-slot="card"]') as HTMLElement | null;

    const measure = () => {
      if (!content) return;
      const contentTop = content.getBoundingClientRect().top;
      const bottomLimit = main
        ? main.getBoundingClientRect().bottom -
          px(getComputedStyle(main).paddingBottom)
        : window.innerHeight;
      // Alto EXTERIOR de la paginación (incluye su margen mt-1, que
      // getBoundingClientRect no cuenta y dejaba ~4px de scroll residual).
      let paginationH = 0;
      const pagEl = paginationRef.current;
      if (pagEl) {
        const s = getComputedStyle(pagEl);
        paginationH =
          pagEl.getBoundingClientRect().height +
          px(s.marginTop) +
          px(s.marginBottom);
      }
      // Chrome inferior REAL de la card (padding + margin + borde), medido.
      let cardBelow = 20;
      if (card) {
        const s = getComputedStyle(card);
        cardBelow =
          px(s.paddingBottom) + px(s.marginBottom) + px(s.borderBottomWidth);
      }
      const available = bottomLimit - contentTop - paginationH - cardBelow;
      setScrollMaxH(available > 80 ? Math.floor(available) : undefined);
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    if (paginationRef.current) ro.observe(paginationRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [sortedData, isLoading, isCardView, displayColumns]);

  // Resultados de agregaciones sobre las filas visibles (página actual).
  // Usa el tipo (kind) seleccionado por el usuario para cada key activa.
  const aggregationResults = useMemo<AggregationResult[]>(() => {
    if (!enableAggregations) return [];
    if (!Array.isArray(sortedData) || sortedData.length === 0) return [];
    const colByKey = new Map(columns.map((c) => [c.key, c]));
    return aggregations.enabledKeys
      .map((key) => {
        const col = colByKey.get(key);
        if (!col || !col.aggregate) return null;
        return computeAggregation(
          sortedData,
          key,
          col.aggregate,
          aggregations.getKind(key),
        );
      })
      .filter((r): r is AggregationResult => r !== null);
  }, [
    enableAggregations,
    sortedData,
    columns,
    aggregations.enabledKeys,
    aggregations,
  ]);

  //  OPTIMIZACIÓN 9: tableKey simplificado
  const tableKey = useMemo(
    () =>
      enableColumnVisibility && tableId
        ? `${tableId}-${visibleColumns.join("-")}`
        : "default",
    [enableColumnVisibility, tableId, visibleColumns],
  );

  // UI optimista solo para el número de página activa: la paginación
  // cambia al instante al hacer click, sin esperar al servidor.
  const [optimisticPage, setOptimisticPage] = useState(
    pagination?.currentPage ?? 1,
  );

  useEffect(() => {
    setOptimisticPage(pagination?.currentPage ?? 1);
  }, [pagination?.currentPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!pagination) return;
      setOptimisticPage(page);
      pagination.onPageChange(page);
    },
    [pagination],
  );

  const effectivePagination = useMemo<
    DataTablePaginationProps | undefined
  >(() => {
    if (!pagination) return undefined;
    return { ...pagination, currentPage: optimisticPage };
  }, [pagination, optimisticPage]);

  //  OPTIMIZACIÓN 10: useEffectEvent para handleColumnHeaderClick
  const handleColumnHeaderClick = useEffectEvent(
    (columnKey: string, event: React.MouseEvent) => {
      const column = displayColumns.find((col) => col.key === columnKey);
      if (!column?.sortable) return;

      const isMultiSort = event.shiftKey || event.ctrlKey;
      toggleSort(columnKey, isMultiSort);
    },
  );

  //  OPTIMIZACIÓN 11: Memoizar getSortIcon
  const getSortIcon = useCallback(
    (columnKey: string) => {
      const direction = getSortDirection(columnKey);
      const priority = getSortPriority(columnKey);

      // Sin sort activo: el header mostrará un ArrowUpDown neutro solo en
      // hover via group-hover; no devolvemos nada aqui para no duplicarlo.
      if (!direction) {
        return null;
      }

      return (
        <div className="flex items-center gap-1">
          {direction === "asc" ? (
            <ArrowUp className="w-4 h-4 text-primary" />
          ) : (
            <ArrowDown className="w-4 h-4 text-primary" />
          )}
          {isMultiSortActive && priority !== null && (
            <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
              {priority + 1}
            </span>
          )}
        </div>
      );
    },
    [getSortDirection, getSortPriority, isMultiSortActive],
  );

  // Botones de acción de una tarjeta (compartido entre vista compacta y mobile).
  const renderRowActions = (
    currentActions: TableAction<T>[],
    row: T,
    compact: boolean,
  ) => {
    if (!currentActions || currentActions.length === 0) return null;
    const showOnlyIcon = currentActions.length > 2;
    const btnClass = compact
      ? "h-7 flex-1 min-w-0 px-2"
      : showOnlyIcon
        ? "flex-1 min-w-0 px-0 "
        : "flex-1 sm:flex-none min-w-0 px-3";

    const renderButton = (action: TableAction<T>) => {
      const button = action.alert ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant={action.variant || "outline"}
              size="sm"
              className={btnClass}
              title={action.label}
            >
              {action.icon}
              {!showOnlyIcon && (
                <span className="ml-2 truncate text-xs">{action.label}</span>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{action.alert.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {action.alert.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("table.actions.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => action.onClick(row)}>
                {t("table.actions.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <Button
          variant={action.variant || "outline"}
          size="sm"
          onClick={() => action.onClick(row)}
          className={btnClass}
          title={action.label}
        >
          {action.icon}
          {!showOnlyIcon && (
            <span className="ml-2 truncate text-xs">{action.label}</span>
          )}
        </Button>
      );

      if (!compact) {
        return <Fragment key={action.id}>{button}</Fragment>;
      }

      return (
        <Tooltip key={action.id}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top">
            <p>{action.label}</p>
          </TooltipContent>
        </Tooltip>
      );
    };

    const content = (
      <div
        className={cn(
          "flex border-t border-border",
          compact
            ? "mt-auto gap-1.5 pt-2"
            : `pt-3 ${currentActions.length > 2 ? "gap-2" : "flex-wrap gap-3"}`,
        )}
      >
        {currentActions.map(renderButton)}
      </div>
    );

    if (!compact) return content;

    return <TooltipProvider>{content}</TooltipProvider>;
  };

  // Render de una fila como tarjeta.
  // compact=true → estilo insights (denso, varias por fila en desktop).
  // compact=false → tarjeta amplia de mobile.
  const renderRowCard = (row: T, index: number, compact = false) => {
    const currentActions = rowActions ? rowActions(row) : actions;
    const rowId = getRowId(row);
    const isSelected = selectedRows.has(rowId);
    const selectable = isRowSelectable ? isRowSelectable(row) : true;
    const extraRowClass = rowClassName?.(row);

    if (compact) {
      return (
        <Card
          key={row.id || row._id || index}
          variant="outline"
          className={cn(
            "flex flex-col !gap-0 !py-0 light:bg-linear-to-tl from-zinc-100 via-zinc-200 to-zinc-50 hover:bg-zinc-200/70 transition-colors dark:bg-zinc-900/90  dark:hover:bg-zinc-950",
            isSelected && "ring-1 ring-primary/50 bg-accent",
            extraRowClass,
          )}
        >
          <CardContent className="flex flex-1 flex-col gap-2 p-3">
            {enableRowSelection && selectable && (
              <div
                className="flex cursor-pointer items-center gap-2"
                onClick={() => handleRowSelection(rowId)}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleRowSelection(rowId)}
                  aria-label={t("table.selection.selectItem", {
                    index: index + 1,
                  })}
                />
              </div>
            )}

            {displayColumns.map((column, colIndex) => {
              const value = column.render
                ? column.render(row[column.key], row)
                : row[column.key];

              if (value === null || value === undefined || value === "")
                return null;

              return (
                <div
                  key={column.key}
                  className="flex items-center justify-between gap-3 text-xs"
                >
                  <span className="shrink-0 text-muted-foreground">
                    {column.label}
                  </span>
                  <div
                    className={
                      colIndex === 0
                        ? "min-w-0 truncate text-right text-sm font-semibold text-foreground"
                        : "min-w-0 truncate text-right font-medium text-foreground"
                    }
                  >
                    {value}
                  </div>
                </div>
              );
            })}

            {renderRowActions(currentActions, row, true)}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        key={row.id || row._id || index}
        className={cn(
          "p-4",
          isSelected && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950",
          extraRowClass,
        )}
      >
        <div className="space-y-3">
          {enableRowSelection && selectable && (
            <div
              className="flex items-center gap-2 pb-2 border-b border-border cursor-pointer"
              onClick={() => handleRowSelection(rowId)}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => handleRowSelection(rowId)}
                aria-label={t("table.selection.selectItem", {
                  index: index + 1,
                })}
              />
              <span className="text-sm text-muted-foreground select-none">
                {isSelected
                  ? t("table.selection.selected")
                  : t("table.selection.select")}
              </span>
            </div>
          )}

          <div className="space-y-3">
            {displayColumns.map((column, colIndex) => {
              const value = column.render
                ? column.render(row[column.key], row)
                : row[column.key];

              if (value === null || value === undefined || value === "")
                return null;

              return (
                <div key={column.key}>
                  {colIndex === 0 ? (
                    <div className="font-bold text-2xl text-foreground truncate pb-3 border-b border-border">
                      {value}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm font-medium text-muted-foreground flex-shrink-0 w-1/3">
                        {column.label}
                      </span>
                      <div className="text-sm text-foreground text-right flex-1 min-w-0 pl-4 flex justify-end items-center">
                        <div className="truncate">{value}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {renderRowActions(currentActions, row, false)}
        </div>
      </Card>
    );
  };

  return (
    <>
      <Card
        className={cn("transition-opacity duration-20 w-full hidden sm:block !py-3")}
      >
        {(title ||
          description ||
          showViewToggle ||
          (enableColumnVisibility && tableId) ||
          (enableAggregations && tableId)) && (
          <CardHeader className="gap-0 pb-1 px-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-4 flex items-center">
                {title && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-flex items-center gap-2 min-w-0">
                          {HeaderIcon && (
                            <HeaderIcon
                              className="h-4 w-4 stroke-[2px] text-muted-foreground flex-shrink-0"
                              aria-hidden="true"
                            />
                          )}
                          <CardTitle className="text-sm truncate cursor-default">
                            {title}
                          </CardTitle>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-md"
                      >
                        <p>{title}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {(offlineFromCache ||
                showViewToggle ||
                (enableColumnVisibility && tableId) ||
                (enableAggregations && tableId)) && (
                <div className="flex items-center gap-1">
                  {offlineFromCache && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex size-7 items-center justify-center rounded-md text-amber-600 dark:text-amber-400">
                            <Icons.WifiOff className="size-4" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          Sin conexión — mostrando datos guardados
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {showViewToggle && (
                    <TableViewToggle
                      viewMode={viewMode}
                      onChange={setViewMode}
                    />
                  )}
                  {enableAggregations && tableId && (
                    <AggregationsToggle
                      available={availableAggregations}
                      enabledKeys={aggregations.enabledKeys}
                      toggle={aggregations.toggle}
                      reset={aggregations.reset}
                      showLabel={false}
                    />
                  )}
                  {enableColumnVisibility && tableId && (
                    <ColumnVisibilityToggle
                      tableId={tableId}
                      columns={columns}
                      visibleColumns={visibleColumns}
                      toggleColumn={toggleColumn}
                      resetToDefaults={resetToDefaults}
                      isColumnVisible={isColumnVisible}
                    />
                  )}
                </div>
              )}
            </div>
          </CardHeader>
        )}
        <CardContent
          ref={cardContentRef}
          className="flex flex-col px-4"
          style={
            {
              "--dt-max": scrollMaxH ? `${scrollMaxH}px` : "80dvh",
            } as React.CSSProperties
          }
        >
          {isLoading ? (
            <div className="max-h-[var(--dt-max)] overflow-auto">
              {isCardView ? (
                <SkeletonCardsGrid
                  columnsCount={skeletonColumns.length}
                  cardsCount={effectivePagination?.pageSize ?? 8}
                />
              ) : (
                <SkeletonTableBody
                  columsNumber={skeletonColumns.length}
                  rowsNumber={effectivePagination?.pageSize ?? 5}
                  columns={skeletonColumns}
                  enableRowSelection={enableRowSelection}
                  tableId={tableId}
                />
              )}
            </div>
          ) : sortedData &&
            Array.isArray(sortedData) &&
            sortedData.length > 0 ? (
            isCardView ? (
              <div className="max-h-[var(--dt-max)] overflow-y-auto">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3 py-1">
                  {sortedData.map((row, index) =>
                    renderRowCard(row, index, true),
                  )}
                </div>
              </div>
            ) : (
              // Wrapper con scroll vive en el <Table> de shadcn (data-slot="table-container")
              // para que sticky-top del thead tenga un ancestro con overflow directo.
              <div ref={tableScrollRef} className="w-full">
                <Table
                  key={tableKey}
                  containerClassName="max-h-[var(--dt-max)] overflow-auto"
                  className={cn("w-max min-w-full", className)}
                >
                  <DataTableHeader
                    displayColumns={displayColumns}
                    enableRowSelection={enableRowSelection}
                    allRowsSelected={allRowsSelected}
                    handleSelectAll={handleSelectAll}
                    handleColumnHeaderClick={handleColumnHeaderClick}
                    getSortIcon={getSortIcon}
                    getSortDirection={getSortDirection}
                    hasActions={(actions?.length ?? 0) > 0 || !!rowActions}
                    tableId={tableId}
                    onReorderColumn={
                      enableColumnReorder ? moveColumn : undefined
                    }
                    enablePin={enableColumnPin}
                    getPinSide={getPinSide}
                    onSetPin={setPin}
                    pinnedStyles={pinnedStyles}
                    selectionSticky={selectionSticky}
                  />

                  <DataTableBody
                    sortedData={sortedData}
                    selectedRows={selectedRows}
                    enableRowSelection={enableRowSelection}
                    displayColumns={displayColumns}
                    handleRowSelection={handleRowSelection}
                    getRowId={getRowId}
                    rowActions={rowActions}
                    tableId={tableId}
                    pinnedStyles={pinnedStyles}
                    selectionSticky={selectionSticky}
                    rowClassName={rowClassName}
                    isRowSelectable={isRowSelectable}
                  />

                  {/* Barra de agregaciones dentro del <tfoot> para que comparta
                      el scroll horizontal de la tabla. Cada pill se coloca en
                      la celda de su columna correspondiente (overflow visible
                      para que pueda sobresalir si es más ancha que la columna).
                      Sticky-bottom para que quede visible al hacer scroll
                      vertical. Las celdas heredan los estilos sticky de las
                      columnas pinneadas. */}
                  {enableAggregations && aggregationResults.length > 0 && (
                    <TableFooter className="sticky bottom-0 z-20 bg-card">
                      <TableRow className="hover:bg-transparent">
                        {enableRowSelection && (
                          <TableCell
                            className={cn(
                              "bg-card border-t border-border",
                              selectionSticky &&
                                "sticky left-0 z-30 bg-card",
                            )}
                          />
                        )}
                        {displayColumns.map((col) => {
                          const result = aggregationResults.find(
                            (r) => r.key === col.key,
                          );
                          const pinStyle = pinnedStyles[col.key];
                          const pinned = !!pinStyle;
                          return (
                            <TableCell
                              key={col.key}
                              style={pinStyle ? { ...pinStyle } : undefined}
                              className={cn(
                                "relative !overflow-visible bg-card border-t border-border p-1 align-middle",
                                pinned &&
                                  "z-15 bg-card group-hover:bg-[color-mix(in_srgb,var(--muted)_50%,var(--card))]",
                              )}
                            >
                              {result && (
                                <AggregationPill
                                  result={result}
                                  onSwitchKind={aggregations.setKind}
                                  onRemove={aggregations.disable}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                        {((actions?.length ?? 0) > 0 || !!rowActions) && (
                          <TableCell className="bg-card border-t border-border" />
                        )}
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </div>
            )
          ) : (
            <Empty className="py-12">
              <EmptyHeader className="gap-0">
                <EmptyMedia>
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
                    {emptyState?.icon ?? (
                      <Inbox className="size-12 opacity-60" />
                    )}
                  </div>
                </EmptyMedia>
                <EmptyTitle>
                  {emptyState?.title || t("table.empty.title")}
                </EmptyTitle>
                <EmptyDescription>
                  {emptyState?.description || t("table.empty.description")}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}

          {/* Paginacion: footer fijo fuera del scroll, se mantiene durante la
              carga para que no desaparezca/salte el layout. */}
          {effectivePagination && (
            <div ref={paginationRef} className="flex-shrink-0 mt-1">
              <AdvancedPagination
                currentPage={effectivePagination.currentPage}
                totalCount={effectivePagination.totalCount}
                pageSize={effectivePagination.pageSize}
                totalCountIsLowerBound={
                  effectivePagination.totalCountIsLowerBound
                }
                onPageChange={handlePageChange}
                showInfo={true}
                compactMobile={false}
                isLoading={isLoading}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vista de cards para mobile sin borde principal */}
      <div className="sm:hidden flex-1 min-h-0 flex flex-col">
        {isLoading ? (
          <SkeletonCardsMobile
            columnsCount={skeletonColumns.length}
            cardsCount={effectivePagination?.pageSize ?? 5}
          />
        ) : sortedData && Array.isArray(sortedData) && sortedData.length > 0 ? (
          <div className="flex flex-col min-h-0 flex-1">
            {(title ||
              description ||
              enableRowSelection ||
              (enableColumnVisibility && tableId)) && (
              <div className="mb-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    {title && (
                      <div className="font-bold text-sm truncate flex items-center gap-2">
                        {HeaderIcon && (
                          <HeaderIcon
                            className="h-4 w-4 stroke-[2px] text-muted-foreground flex-shrink-0"
                            aria-hidden="true"
                          />
                        )}
                        {title}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {enableRowSelection && (
                        <Checkbox
                          checked={allRowsSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label={t("table.selection.selectAll")}
                        />
                      )}
                      {enableRowSelection && (
                        <span className="text-xs text-muted-foreground">
                          {selectedRows.size} / {sortedData.length}
                        </span>
                      )}
                      {enableColumnVisibility && tableId && (
                        <ColumnVisibilityToggle
                          tableId={tableId}
                          columns={columns}
                          visibleColumns={visibleColumns}
                          toggleColumn={toggleColumn}
                          resetToDefaults={resetToDefaults}
                          isColumnVisible={isColumnVisible}
                          showLabel={false}
                        />
                      )}
                    </div>
                  </div>

                  {description && (
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {description}
                    </div>
                  )}
                </div>
              </div>
            )}

            <ScrollArea className="min-h-0 flex-1">
              <div className="space-y-3 pb-4">
                {sortedData.map((row, index) => renderRowCard(row, index))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <Empty className="py-6 px-4 gap-3">
            <EmptyHeader className="gap-1">
              <EmptyMedia className="mb-0">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center text-muted-foreground">
                  {emptyState?.icon ?? <Inbox className="size-10 opacity-60" />}
                </div>
              </EmptyMedia>
              <EmptyTitle className="text-base">
                {emptyState?.title || t("table.empty.title")}
              </EmptyTitle>
              <EmptyDescription>
                {emptyState?.description || t("table.empty.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}

        {effectivePagination && (
          <div className="flex-shrink-0 mt-2 pt-3 border-t border-border">
            {enableAggregations && (
              <AggregationsBar
                results={aggregationResults}
                onSwitchKind={aggregations.setKind}
                onRemove={aggregations.disable}
                className="!px-0 !border-b-0 !bg-transparent mb-2"
              />
            )}
            <AdvancedPagination
              currentPage={effectivePagination.currentPage}
              totalCount={effectivePagination.totalCount}
              pageSize={effectivePagination.pageSize}
              totalCountIsLowerBound={
                effectivePagination.totalCountIsLowerBound
              }
              onPageChange={handlePageChange}
              showInfo={true}
              compactMobile={true}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>

      {/* Bulk action bar — portal to body, shown when rows are selected */}
      {enableRowSelection && bulkActions && bulkActions.length > 0 && (
        <BulkActionBar
          selectedRows={selectedRows as Set<string>}
          onClearSelection={() => handleSelectionChange(new Set())}
          actions={bulkActions}
          label={bulkActionsLabel}
          selectionRows={(Array.isArray(sortedData) ? sortedData : []).filter(
            (row: any) =>
              (selectedRows as Set<string | number>).has(String(row[rowIdField])),
          )}
        />
      )}
    </>
  );
}
